import { AlertBox, Box, Button, FlexBox, Icon, Link, Typography } from '@vp/swan';
import React, { useContext, useEffect, useState } from 'react';
import ConversationEvent from '../ConversationEvent/ConversationEvent';
import { useCustomTracking } from '../../hooks/custom-hooks/useTracking';
import FeedbackPopover from '../MessageFeedback/FeedbackPopover';
import './EndChatFeedback.styles.scss';

import { CAContext } from '../../contexts/CAContext';
import { getPrivacyPolicyLink } from '../../config/privacy-link';
import { MiawChatContext } from '../../contexts/MiawChatContext';

interface IEndChatFeedbackProps {
  onStartNewChat?: () => void;
  isSessionExpired?: boolean;
}

const EndChatFeedback: React.FC<IEndChatFeedbackProps> = ({ onStartNewChat, isSessionExpired }) => {
  const {
    chatState: { endChatInfo, locale },
  } = useContext(CAContext);
  const { sendTrackingEvent } = useCustomTracking();

  const { setState } = useContext(MiawChatContext);
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const [showAfterFeedback, setShowAfterFeedback] = useState<boolean>(false);
  const [activeButton, setActiveButton] = useState<'like' | 'dislike' | null>(null);

  const onStartNewChatHandler = () => {
    setState((prev) => ({ ...prev, isRoutingError: false, isChatEndedByAgent: false }));
    sendTrackingEvent('Chatbot Clicked', 'Chatbot:CTA', 'startNewChatCtaClicked');
    if (onStartNewChat) {
      onStartNewChat();
    }
  };

  const handleSendFeedback = (feedbackMessage: string) => {
    sendTrackingEvent('Chatbot Clicked', 'Chatbot:Feedback', 'endChatFeedbackSubmitted', undefined, {
      feedbackText: feedbackMessage,
      reaction: 'down',
      chatbotSessionId: endChatInfo?.sessionId || '',
    });
    setShowPopover(false);
    setShowAfterFeedback(true);
    // setShowFeedback(false);
  };

  const handleLike = () => {
    setShowPopover(false);
    setActiveButton('like');
    sendTrackingEvent('Chatbot Clicked', 'Chatbot:Feedback', 'endChatFeedbackReaction', undefined, {
      reaction: 'up',
      chatbotSessionId: endChatInfo?.sessionId || '',
    });
    setShowAfterFeedback(true);
  };

  const handleDislike = () => {
    setShowPopover(true);
    setActiveButton('dislike');
    sendTrackingEvent('Chatbot Clicked', 'Chatbot:Feedback', 'endChatFeedbackReaction', undefined, {
      reaction: 'down',
      chatbotSessionId: endChatInfo?.sessionId || '',
    });
  };

  useEffect(() => {
    if (showPopover) {
      sendTrackingEvent('Chatbot Viewed', 'Chatbot:Feedback', 'endChatFeedbackTextOpened', undefined, {
        chatbotSessionId: endChatInfo?.sessionId || '',
      });
    }
  }, [showPopover]);

  const handleClosePopover = () => {
    setShowPopover(false);
  };
  return (
    <FlexBox flexDirection={'column'} compactMode alignItems={'center'} justifyContent={'start'} style={{ width: '100%', height: '100%' }}>
      <FlexBox compactMode marginBottom="7" flexDirection={'column'} alignItems={'center'} justifyContent={'center'} style={{ width: '100%' }}>
        {isSessionExpired && (
          <Box>
            <AlertBox skin={'warning'} mb="4" m="5" style={{ width: 'fit-content', maxWidth: 'fit-content' }}>
              The chat ended after 1 hour without messages. You can start a new one anytime.{' '}
              <Link
                render={(p) => (
                  <Button skin="link" className={p.className} onClick={onStartNewChatHandler}>
                    {p.children}
                  </Button>
                )}
              >
                Start chat
              </Link>
            </AlertBox>
          </Box>
        )}
        <ConversationEvent
          eventDetails={isSessionExpired ? 'Conversation ended automatically' : 'Conversation ended'}
          timeStamp={new Date().getTime()}
          width="100%"
          paddingX="5"
        />
        {endChatInfo?.sessionType === 'chatbot' && !showAfterFeedback && (
          <>
            <Typography fontSkin="title-section" mb={'3'} mt="7">
              Was Vista Assistant helpful?
            </Typography>
            <Link href={getPrivacyPolicyLink(locale, true)} mb="7" target="_blank">
              Learn how we use your feedback.{' '}
            </Link>
            <FlexBox gap="between-actions">
              <Button onClick={handleLike} className={`chat-feedback-button ${activeButton === 'like' ? 'active' : ''}`}>
                <Icon iconType={'like'} />
                Yes
              </Button>
              <Button onClick={handleDislike} className={`chat-feedback-button ${activeButton === 'dislike' ? 'active' : ''}`}>
                <Icon iconType={'dislike'} />
                No
              </Button>
            </FlexBox>
          </>
        )}
      </FlexBox>{' '}
      {endChatInfo?.sessionType == 'chatbot' && showPopover && !showAfterFeedback && (
        <Box mb="7" style={{ width: '100%' }} px="5">
          <FeedbackPopover
            popoverType="embed"
            onClose={handleClosePopover}
            onSendFeedback={handleSendFeedback}
            popoverDisplayData={{
              description: ' ',
              heading: 'Help us improve',
              placeholder: 'Tell us what went wrong',
              sendButtonText: 'Send feedback',
            }}
            showCancelButton={false}
          />
        </Box>
      )}
      {endChatInfo?.sessionType === 'chatbot' && showAfterFeedback && (
        <Box my="7">
          <Typography fontSkin="title-section" mb="2" textAlign={'center'}>
            Thanks for your feedback!
          </Typography>
          <Typography fontSkin="body-standard" textAlign={'center'}>
            You just made me a little smarter.
          </Typography>{' '}
        </Box>
      )}
      <Button skin={'primary'} onClick={onStartNewChatHandler}>
        Start new chat
      </Button>
    </FlexBox>
  );
};

export default EndChatFeedback;
